// AICredits.js — Teacher AI Credits Management Page
// 1 Credit = 1 Essay Grading (تصحيح إجابة مقالية واحدة)

import React, { useEffect, useState, useCallback } from 'react';
import { aiCreditsAPI } from '../services/api';
import '../styles/AICredits.css';
import {
  FaBrain,
  FaWhatsapp,
  FaHistory,
  FaPlus,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBan,
  FaArrowUp,
  FaArrowDown,
  FaSpinner,
} from 'react-icons/fa';

// ─────── Helpers ───────
const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric' });
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('ar-EG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const STATUS_MAP = {
  pending: { label: 'معلق', icon: <FaClock />, cls: 'pending' },
  approved: { label: 'مقبول', icon: <FaCheckCircle />, cls: 'approved' },
  rejected: { label: 'مرفوض', icon: <FaTimesCircle />, cls: 'rejected' },
  cancelled: { label: 'ملغي', icon: <FaBan />, cls: 'cancelled' },
};

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '+4915209451229';

// ─────── Component ───────
const AICredits = () => {
  const [data, setData] = useState(null);   // { credits, credits_used, transactions, requests }
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successInfo, setSuccessInfo] = useState(null);  // { request_id, package }
  const [txFilter, setTxFilter] = useState('all');    // all | credit | debit
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [credRes, pkgRes] = await Promise.all([
        aiCreditsAPI.getMyCredits(),
        aiCreditsAPI.getPackages(),
      ]);
      setData(credRes.data);
      setPackages(pkgRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Submit Request ───
  const handleSubmit = async () => {
    if (!selectedPkg) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await aiCreditsAPI.submitRequest({
        package_id: selectedPkg.id,
        teacher_note: note.trim() || undefined,
      });
      setSuccessInfo({ request_id: res.data.request_id, pkg: selectedPkg });
      setNote('');
      setSelectedPkg(null);
      fetchAll();
    } catch (e) {
      setError(e.response?.data?.message || 'حدث خطأ، حاول مجدداً');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Cancel Request ───
  const handleCancel = async (id) => {
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الطلب؟')) return;
    try {
      await aiCreditsAPI.cancelRequest(id);
      fetchAll();
    } catch (e) {
      alert(e.response?.data?.message || 'فشل الإلغاء');
    }
  };

  // ─── Derived ───
  const credits = data?.credits ?? 0;
  const creditsUsed = data?.credits_used ?? 0;
  const total = credits + creditsUsed;
  const usedPct = total > 0 ? Math.min(100, (creditsUsed / total) * 100) : 0;

  const filteredTx = (data?.transactions ?? []).filter((t) => {
    if (txFilter === 'all') return true;
    return t.type === txFilter;
  });

  const hasPending = (data?.requests ?? []).some((r) => r.status === 'pending');

  if (loading) {
    return (
      <div className="ai-credits-page">
        <div className="aip-loading">
          <FaSpinner style={{ animation: 'spin 1s linear infinite', marginLeft: 8 }} />
          جارٍ تحميل بياناتك...
        </div>
      </div>
    );
  }

  return (
    <div className="ai-credits-page">
      {/* ── Header ── */}
      <div className="aip-header">
        <h1><FaBrain /> رصيد الذكاء الاصطناعي</h1>
        <p>
          إدارة رصيد تصحيح الأسئلة المقالية — <strong>1 كريدت = تصحيح إجابة مقالية واحدة</strong>
        </p>
      </div>

      {/* ── Balance Card ── */}
      <div className="aip-balance-card">
        <div className="aip-balance-info">
          <div className="aip-balance-label">رصيدك الحالي</div>
          <div className="aip-balance-value">{credits.toLocaleString()}</div>
          <div className="aip-balance-unit">كريدت متاح للتصحيح</div>

          <div className="aip-balance-meta">
            <div className="aip-balance-meta-item">
              <span>تصحيحات مُجراة</span>
              <span>{creditsUsed.toLocaleString()}</span>
            </div>
            <div className="aip-balance-meta-item">
              <span>إجمالي الكريدت</span>
              <span>{total.toLocaleString()}</span>
            </div>
          </div>

          {total > 0 && (
            <div className="aip-usage-bar-wrap">
              <div className="aip-usage-bar-label">
                {creditsUsed.toLocaleString()} مستخدم من أصل {total.toLocaleString()}
              </div>
              <div className="aip-usage-bar-bg">
                <div className="aip-usage-bar-fill" style={{ width: `${usedPct}%` }} />
              </div>
            </div>
          )}
        </div>
        <div className="aip-balance-icon">🤖</div>
      </div>

      {/* ── Success Alert after request ── */}
      {successInfo && (
        <div className="aip-success-alert">
          <h4>✅ تم إرسال طلبك بنجاح!</h4>
          <p>
            لإتمام عملية الشحن، تواصل مع الإدارة عبر واتساب وأرسل <strong>رقم الطلب</strong> التالي:
          </p>
          <div className="aip-req-number">#{successInfo.request_id}</div>
          <p style={{ marginTop: '0.5rem' }}>
            الباقة: <strong>{successInfo.pkg?.name}</strong> —{' '}
            <strong>{successInfo.pkg?.credits} كريدت</strong> مقابل{' '}
            <strong>${successInfo.pkg?.price}</strong>
          </p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(
              `مرحباً، أريد شحن رصيد الذكاء الاصطناعي.\nرقم الطلب: #${successInfo.request_id}\nالباقة: ${successInfo.pkg?.name} (${successInfo.pkg?.credits} كريدت - $${successInfo.pkg?.price})`
            )}`}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              marginTop: '0.75rem', background: '#25d366', color: '#fff',
              padding: '8px 18px', borderRadius: '10px', fontWeight: 700,
              fontSize: '0.9rem', textDecoration: 'none', transition: 'opacity .2s',
            }}
          >
            <FaWhatsapp size={18} /> تواصل عبر واتساب
          </a>
          <button
            onClick={() => setSuccessInfo(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '1rem', color: '#065f46', fontSize: '0.82rem' }}
          >
            إخفاء
          </button>
        </div>
      )}

      {/* ── Packages ── */}
      <div className="aip-card" style={{ marginBottom: '1.5rem' }}>
        <div className="aip-card-title"><FaPlus /> اختر باقة لشحن رصيدك</div>
        <div className="aip-packages-grid">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`aip-pkg-card ${selectedPkg?.id === pkg.id ? 'selected' : ''}`}
              onClick={() => setSelectedPkg(pkg)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedPkg(pkg)}
            >
              {pkg.badge && <div className="aip-pkg-badge">{pkg.badge}</div>}
              <div className="aip-pkg-name">{pkg.name}</div>
              <div className="aip-pkg-credits">{pkg.credits.toLocaleString()}</div>
              <div className="aip-pkg-credits-label">تصحيح مقالي</div>
              <div className="aip-pkg-price">${pkg.price}</div>
              {pkg.description && <div className="aip-pkg-desc">{pkg.description}</div>}
            </div>
          ))}
        </div>

        {/* Request Form */}
        {selectedPkg && (
          <div className="aip-request-form" style={{ border: 'none', padding: 0 }}>
            <h3><FaWhatsapp style={{ color: '#25d366' }} /> إرسال طلب الشحن</h3>

            <div className="aip-info-box">
              أنت على وشك طلب شحن{' '}
              <strong>{selectedPkg.credits} كريدت</strong> (باقة {selectedPkg.name}) بسعر{' '}
              <strong>${selectedPkg.price}</strong>.<br />
              بعد إرسال الطلب ستحصل على <strong>رقم الطلب</strong>، أرسله عبر{' '}
              <span className="aip-wa-number"><FaWhatsapp /> واتساب</span>{' '}
              مع إثبات الدفع. سيتم إضافة رصيدك خلال 24 ساعة.
            </div>

            {hasPending && (
              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1rem', color: '#92400e', fontSize: '0.88rem' }}>
                ⚠️ لديك طلب معلق بالفعل. يُنصح بانتظار معالجته قبل إرسال طلب جديد.
              </div>
            )}

            <div className="aip-form-group">
              <label>ملاحظة للإدارة (اختياري)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="مثال: سأتواصل عبر واتساب اليوم لتأكيد الدفع..."
                maxLength={300}
              />
            </div>

            {error && (
              <div style={{ color: '#dc2626', background: '#fee2e2', borderRadius: 10, padding: '0.75rem', marginBottom: '1rem', fontSize: '0.88rem' }}>
                ❌ {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="aip-submit-btn" onClick={handleSubmit} disabled={submitting}>
                {submitting ? (
                  <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> جارٍ الإرسال...</>
                ) : (
                  <><FaWhatsapp /> إرسال الطلب</>
                )}
              </button>
              <button
                style={{ padding: '0.9rem 1.5rem', border: '1px solid var(--border,#e5e7eb)', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontFamily: 'Cairo,sans-serif', fontSize: '0.9rem', color: 'var(--text-muted,#6b7280)' }}
                onClick={() => { setSelectedPkg(null); setError(''); }}
              >
                إلغاء
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Grid: Requests + Transactions ── */}
      <div className="aip-grid">
        {/* Requests History */}
        <div className="aip-card">
          <div className="aip-card-title"><FaHistory /> طلبات الشحن السابقة</div>
          {data?.requests?.length > 0 ? (
            <div className="aip-requests-list">
              {data.requests.map((r) => {
                const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
                return (
                  <div key={r.id} className="aip-request-item">
                    <div className="aip-request-item-left">
                      <div className="aip-request-pkg">
                        {r.package_name || `باقة #${r.package_id}`}
                        {' '}<span className="aip-req-id">#{r.id}</span>
                      </div>
                      <div className="aip-request-detail">
                        {r.credits_requested} كريدت — ${r.amount} — {fmt(r.created_at)}
                      </div>
                      {r.admin_note && (
                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                          ملاحظة الإدارة: {r.admin_note}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                      <span className={`aip-status-badge ${st.cls}`}>
                        {st.icon} {st.label}
                      </span>
                      {r.status === 'pending' && (
                        <button className="aip-cancel-btn" onClick={() => handleCancel(r.id)}>
                          إلغاء
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="aip-empty">لا توجد طلبات شحن سابقة</div>
          )}
        </div>

        {/* Transactions */}
        <div className="aip-card">
          <div className="aip-card-title"><FaHistory /> سجل الحركات</div>
          <div className="aip-tabs">
            {[['all', 'الكل'], ['credit', 'إضافات'], ['debit', 'خصومات']].map(([v, l]) => (
              <button
                key={v}
                className={`aip-tab ${txFilter === v ? 'active' : ''}`}
                onClick={() => setTxFilter(v)}
                type="button"
              >
                {l}
              </button>
            ))}
          </div>
          {filteredTx.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="aip-tx-table">
                <thead>
                  <tr>
                    <th>النوع</th>
                    <th>الكريدت</th>
                    <th>الرصيد</th>
                    <th>التفاصيل</th>
                    <th>التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTx.map((tx) => (
                    <tr key={tx.id}>
                      <td>
                        {tx.type === 'credit'
                          ? <span style={{ color: '#059669' }}><FaArrowUp size={11} /> إضافة</span>
                          : <span style={{ color: '#dc2626' }}><FaArrowDown size={11} /> خصم</span>}
                      </td>
                      <td className={tx.type === 'credit' ? 'aip-tx-credit' : 'aip-tx-debit'}>
                        {tx.type === 'credit' ? '+' : '-'}{tx.amount}
                      </td>
                      <td style={{ fontWeight: 600 }}>{tx.balance_after}</td>
                      <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{tx.description || '—'}</td>
                      <td style={{ fontSize: '0.78rem', direction: 'ltr', textAlign: 'left' }}>
                        {fmtDateTime(tx.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="aip-empty">لا توجد حركات</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AICredits;
